import { useMemo } from "react";
import { bool, object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { MONTH_WEEKS, MinAvgMaxKeys } from "@repo/utils";
import {
  useEstimateStructure,
  useEstimateEditorStore,
  useSummaryTimeInputTooltipAlert,
  useVisibilityModeCellClassName,
  useEstimateEditorSettings,
  ValueCell,
  ResetValueButton,
  TimelineSettings,
} from "@tools";
import { Grid } from "@material-ui/core";
import classnames from "classnames";

export const TimeCell = observer(({
  element,
  displayCellContent,
  extendPadding,
  ...props
}) => {
  const { t } = useTranslation();
  const { visibilityMode } = useEstimateEditorStore();
  const { allowEdition, useProfitability } = useEstimateEditorSettings();
  const structure = useEstimateStructure();
  const tooltip = useSummaryTimeInputTooltipAlert(allowEdition);
  const className = useVisibilityModeCellClassName(structure.visibility.hideSummaryTime);
  
  const { displayAnyValue, displayTotalTwoValues, displayWorkTypeTwoValues } = structure;
  const { useWeekTime, isTimelineTableUsed, maxValuesAsBuffer } = structure.settings;
  const {
    displayTime,
    displayTimeMax,
    hasFixedTime,
    hasFixedPrice,
    hasResources,
  } = element;
  
  const editable = allowEdition && !hasResources && !useProfitability && !isTimelineTableUsed;
  
  const showMaxValue = useMemo(() => (
    !maxValuesAsBuffer && (
      isTimelineTableUsed
        ? displayTotalTwoValues
        : displayWorkTypeTwoValues[element.workTypeId]
    ) && (editable || displayTime !== displayTimeMax)
  ), [ displayTotalTwoValues, editable, displayTime, displayTimeMax, maxValuesAsBuffer ]);
  
  const handleTimeChange = (time, isMax) => {
    let key = MinAvgMaxKeys.AVG;
    if(isMax)
      key = MinAvgMaxKeys.MAX;
    else if(!maxValuesAsBuffer && showMaxValue)
      key = MinAvgMaxKeys.MIN;
    
    element.setFixedTime({ [key]: time * (useWeekTime ? 1 : MONTH_WEEKS) });
  };
  
  const handleReset = () => {
    element.resetFixedTime();
  };
  
  return (
    <ValueCell
      size="large"
      showDecimals
      emptyInputs={hasFixedPrice || !displayAnyValue}
      value={displayTime}
      valueMax={displayTimeMax}
      displayMax={showMaxValue}
      onValueChange={handleTimeChange}
      displayContent={displayCellContent}
      warning={hasFixedTime}
      editable={editable}
      className={className}
      extendPadding={extendPadding || allowEdition || visibilityMode}
      textPresetClass="preset-summaryText"
      inputClassName="input-time"
      innerClassName={classnames((allowEdition || visibilityMode) && "relative")}
      { ...props }
      { ...tooltip }
    >
      {
        displayCellContent && allowEdition &&
        <Grid
          item container
          direction="column"
          justifyContent="center"
          alignItems="center"
          className="absolute w-max"
          style={{ right: -16 }}
        >
          {
            hasFixedTime &&
            <ResetValueButton action={handleReset}>
              {t("views.editor.revert_value")}
            </ResetValueButton>
          }
          <TimelineSettings
            selectedWorkType={element.id}
            className="button-wta-settings text-sm noDrag cellHoverIcon opq"
          />
        </Grid>
      }
    </ValueCell>
  );
});

TimeCell.propTypes = {
  element: object.isRequired,
  displayCellContent: bool,
  extendPadding: bool,
};
